import { useCallback } from 'react';

import { isCypressUserEmail } from 'src/lib/isCypressUser';

const PORTAL_ID = '244218062';

export const HUBSPOT_FORM_IDS = {
  HIGHER_LIMIT_REQUEST: '57aa8131-e480-4c71-a0c4-1eedf0fd66a7',
  UPGRADE_CONTACT: 'fe8bcebd-97e4-4cab-9ad8-a06a2089f58f',
  GRAPHOS_FREE_SIGNUP: 'a2ae7df8-4349-4eb1-9c75-b46aeb0bc243',
} as const;

interface HubSpotField {
  objectTypeId: string;
  name: string;
  value: string;
}

interface SubmitHubSpotFormArgs {
  formId: string;
  fields: HubSpotField[];
  hutk?: string;
  pageUri?: string;
  pageName?: string;
}

interface UTM {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  referrer?: string;
}

interface PushHubSpotLeadArgs {
  formId: string;
  email: string;
  fullName: string;
  userId?: string;
  orgId?: string;
  companyUrl?: string;
  phone?: string;
  message?: string;
  utm?: null | UTM;
  hutk?: string;
}

/**
 * Creates a HubSpot contact field with the standard contact objectTypeId.
 */
const contactField = (name: string, value?: string): HubSpotField | null => {
  if (value === undefined || value === null) return null;
  return { objectTypeId: '0-1', name, value };
};

/**
 * Creates a HubSpot company field with the standard company objectTypeId.
 */
const companyField = (name: string, value?: string): HubSpotField | null => {
  if (value === undefined || value === null) return null;
  return { objectTypeId: '0-2', name, value };
};

/**
 * Submits a form to HubSpot's Forms API v3.
 * https://developers.hubspot.com/docs/api/marketing/forms
 */
const submitHubSpotForm = async ({
  formId,
  fields,
  hutk,
  pageUri,
  pageName,
}: SubmitHubSpotFormArgs): Promise<boolean> => {
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${formId}`;

  const body: Record<string, unknown> = {
    fields,
    context: {
      ...(hutk && { hutk }),
      pageUri: pageUri || window.location.href,
      pageName: pageName || document.title,
    },
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Hook that returns an async function for pushing leads to HubSpot.
 * Replaces the former `useMarketo` hook.
 *
 * Usage:
 * ```ts
 * const pushHubSpotLead = useHubSpot();
 * await pushHubSpotLead({
 *   formId: HUBSPOT_FORM_IDS.HIGHER_LIMIT_REQUEST,
 *   email: 'user@example.com',
 *   fullName: 'Jane Doe',
 * });
 * ```
 */
export const useHubSpot = () => {
  return useCallback(
    async ({
      formId,
      email,
      fullName,
      userId,
      orgId,
      companyUrl,
      phone,
      message,
      utm,
      hutk,
    }: PushHubSpotLeadArgs): Promise<boolean> => {
      // Skip submission for Cypress test users
      if (isCypressUserEmail(email)) {
        return true;
      }

      const [firstName, ...otherNames] = fullName.split(' ');
      // Last name must be passed for Salesforce sync.
      // If no last name exists, use first name as last and leave first empty.
      const lastName = otherNames.join(' ');

      const fields = [
        contactField('firstname', !lastName ? '' : firstName),
        contactField('lastname', !lastName ? firstName : lastName),
        contactField('email', email),
        contactField('phone', phone),
        contactField('lead_message_long', message),
        companyField('name', companyUrl),
        contactField('utm_source', utm?.utmSource),
        contactField('utm_medium', utm?.utmMedium),
        contactField('utm_campaign', utm?.utmCampaign),
        contactField('utm_content', utm?.utmContent),
        contactField('utm_term', utm?.utmTerm),
        contactField('referrer_original', utm?.referrer),
        contactField('studio_user_id', userId),
        contactField('studio_organization_id', orgId),
      ].filter((field): field is HubSpotField => field !== null);

      return submitHubSpotForm({
        formId,
        fields,
        hutk,
      });
    },
    [],
  );
};
